import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import { handleError } from '@domain-group/fe-helper';
import { Modal } from '@domain-group/fe-blueprint-ui/modal';
import { Button, LinkButton } from '@domain-group/fe-blueprint-ui/button';
import { Message, MessageTray } from '@domain-group/fe-blueprint-ui/message';
import {
  ProfileIcon,
  EmailIcon,
  PhoneIcon,
  PinIcon,
  AgentFilledIcon,
  ArrowRightIcon,
} from '@domain-group/fe-co-icon';
import Illustration from '@domain-group/fe-co-brand-illustrations';
import Avatar, { isNoAgentPhoto } from '../shared/avatar';
import { PhoneNumberReveal } from '../contact-card-2026/contact-card-2026';
import { CheckboxField, TextField } from '../shared/form-fields';
import PrivacyPolicy from '../shared/privacy-policy';
import { SEND_ENQUIRY } from '../contact-enquiry-form/components/funnel/process-send-enquiry';
import { sanitizeForEmail } from '../../utils/sanitize-for-email';
import { joinEnquiryMessage } from './join-enquiry-message';
import { getEnquirySuccessSuburb, SuburbLink } from './get-success-suburb';
import {
  enquiryValidationSchema,
  ENQUIRY_FORM_INITIAL_VALUES,
  ENQUIRY_TYPE_OPTIONS,
  EnquiryFormValues,
} from './enquiry-validation';
import * as styles from './enquiry-modal-2026.style';

type Stage = 'form' | 'sending' | 'success';

export interface EnquiryModal2026Props {
  contactId: number;
  agentName: string;
  jobTitle?: string | null;
  agencyName?: string | null;
  profilePhoto?: string | null;
  telephone?: string | null;
  agencySuburb: SuburbLink | null;
  // Threaded through from the SRP profile link's `?searchSuburb=` query
  // param, when the visitor arrived from a search - see get-success-suburb.ts.
  searchSuburbSlug?: string | null;
  onClose: () => void;
}

const EnquiryModalHeader = ({
  agentName,
  jobTitle,
  agencyName,
  profilePhoto,
  telephone,
}: Pick<
  EnquiryModal2026Props,
  'agentName' | 'jobTitle' | 'agencyName' | 'profilePhoto' | 'telephone'
>): JSX.Element => (
  <div css={styles.header}>
    <h2 css={styles.heading}>Contact {agentName}</h2>
    <div css={styles.headerIdentity}>
      {profilePhoto && !isNoAgentPhoto(profilePhoto) ? (
        <Avatar
          css={styles.avatarImage}
          imageUrl={profilePhoto}
          imageTitle={agentName}
          shape="square"
          size="custom"
        />
      ) : (
        <div
          css={styles.avatarPlaceholder}
          data-testid="enquiry-modal-2026__avatar-placeholder"
        >
          <AgentFilledIcon
            viewBox="0 0 18 20"
            css={styles.avatarPlaceholderIcon}
          />
        </div>
      )}
      <div css={styles.headerDetails}>
        {jobTitle && <p css={styles.headerJobTitle}>{jobTitle}</p>}
        {agencyName && <p css={styles.subheading}>{agencyName}</p>}
      </div>
      {telephone && (
        // Non-flex wrapper: PhoneNumberReveal's button hardcodes flex:1,
        // which is correct in the Contact Card's own CTA row but would
        // stretch it to fill this row instead of sizing to its content.
        <div css={styles.headerPhone}>
          <PhoneNumberReveal
            telephone={telephone}
            name={agentName}
            appearance="outlineAccent"
            hideLabelOnMobile
          />
        </div>
      )}
    </div>
  </div>
);

type EnquiryFormik = ReturnType<typeof useFormik<EnquiryFormValues>>;

const EnquiryForm = ({
  formik,
  submissionFailed,
  onHideError,
}: {
  formik: EnquiryFormik;
  submissionFailed: boolean;
  onHideError: () => void;
}): JSX.Element => {
  const { values, errors, submitCount, setFieldValue, handleSubmit } = formik;

  // Errors only ever show after a submit attempt, never on blur/as-you-type
  // (ticket requirement) - gate purely on submitCount, not formik's touched.
  const fieldError = (field: keyof EnquiryFormValues): string | undefined =>
    submitCount > 0 ? (errors[field] as string | undefined) : undefined;

  const noop = () => {};

  return (
    <form
      onSubmit={handleSubmit}
      css={styles.enquiriesSection}
      data-testid="enquiry-modal-2026__form"
    >
      <div css={styles.formColumns}>
        <div css={styles.formColumn}>
          <div css={styles.section}>
            <p css={styles.groupLabel}>My enquiries</p>
            <hr css={styles.groupDivider} />
            <CheckboxField
              fieldName="enquiryTypes"
              title="My enquiries"
              hiddenTitle
              options={ENQUIRY_TYPE_OPTIONS}
              // checkbox-field.js infers `selected` as `never[]` from its
              // untyped `= []` default param - cast at this boundary rather
              // than widening the shared JS file's inferred type.
              selected={values.enquiryTypes as never[]}
              onChange={setFieldValue}
              onBlur={noop}
              itemStyles={styles.checkboxItem}
            />
          </div>

          <div css={styles.section}>
            <p css={styles.groupLabelNoDivider}>Message</p>
            <TextField
              fieldName="message"
              label="Message"
              hideLabel
              placeholder="What can we help you with?"
              value={values.message}
              onChange={setFieldValue}
              onBlur={noop}
              multiline
              rows={4}
              marginless
            />
          </div>
        </div>

        <div css={styles.formColumn}>
          <div css={styles.section}>
            <p css={styles.groupLabel}>My contact details</p>
            <hr css={styles.groupDivider} />
            <TextField
              fieldName="firstName"
              label="First name"
              hideLabel
              placeholder="First name*"
              required
              value={values.firstName}
              onChange={setFieldValue}
              onBlur={noop}
              errors={fieldError('firstName')}
              icon={<ProfileIcon />}
            />
            <TextField
              fieldName="lastName"
              label="Last name"
              hideLabel
              placeholder="Last name*"
              required
              value={values.lastName}
              onChange={setFieldValue}
              onBlur={noop}
              errors={fieldError('lastName')}
              icon={<ProfileIcon />}
            />
            <TextField
              fieldName="email"
              label="Email"
              hideLabel
              placeholder="Email*"
              type="email"
              required
              value={values.email}
              onChange={setFieldValue}
              onBlur={noop}
              errors={fieldError('email')}
              icon={<EmailIcon />}
            />
            <TextField
              fieldName="phone"
              label="Phone"
              hideLabel
              placeholder="Phone (optional)"
              type="tel"
              value={values.phone}
              onChange={setFieldValue}
              onBlur={noop}
              icon={<PhoneIcon />}
            />
            <TextField
              fieldName="postcode"
              label="Postcode"
              hideLabel
              placeholder="Postcode*"
              required
              value={values.postcode}
              onChange={setFieldValue}
              onBlur={noop}
              errors={fieldError('postcode')}
              icon={<PinIcon />}
            />
          </div>

          <Button type="submit" css={styles.submitButton}>
            Send enquiry
          </Button>
        </div>
      </div>

      {submissionFailed && (
        <div
          css={styles.errorBanner}
          data-testid="enquiry-modal-2026__error-banner"
        >
          <MessageTray>
            <Message type="error" onCloseClick={onHideError}>
              There was a problem sending your enquiry, please try again
            </Message>
          </MessageTray>
        </div>
      )}

      <PrivacyPolicy compact />
    </form>
  );
};

const SendingPlaceholder = (): JSX.Element => (
  <div
    css={styles.sendingPlaceholder}
    data-testid="enquiry-modal-2026__sending"
  />
);

const SuccessState = ({
  suburb,
  onDone,
}: {
  suburb: SuburbLink | null;
  onDone: () => void;
}): JSX.Element => (
  <div css={styles.successSection} data-testid="enquiry-modal-2026__success">
    <h2 css={styles.successHeading}>Thanks for your enquiry!</h2>
    <div css={styles.successDriver}>
      <Illustration
        type="spot"
        shape="circle"
        name="equityHome"
        size="regular"
      />
      <div css={styles.successCopy}>
        <p css={styles.successCrossSell}>
          Curious what your home is worth? Get an instant property value
          estimate and see comparable sales
        </p>
        <LinkButton
          appearance="link"
          href="https://www.domain.com.au/owners"
          target="_blank"
          rel="noopener noreferrer"
          icon={<ArrowRightIcon />}
          iconPosition="right"
        >
          Track your property
        </LinkButton>
      </div>
    </div>
    <div css={styles.successCtaGroup}>
      <Button
        type="button"
        appearance="ghostPrimary"
        onClick={onDone}
        data-testid="enquiry-modal-2026__cta-done"
      >
        Done
      </Button>
      {suburb && (
        <LinkButton
          href={`/real-estate-agents/${suburb.slug}/`}
          data-testid="enquiry-modal-2026__cta-more-agents"
        >
          More agents in {suburb.name}
        </LinkButton>
      )}
    </div>
  </div>
);

const EnquiryModal2026 = ({
  contactId,
  agentName,
  jobTitle,
  agencyName,
  profilePhoto,
  telephone,
  agencySuburb,
  searchSuburbSlug,
  onClose,
}: EnquiryModal2026Props): JSX.Element => {
  const [stage, setStage] = useState<Stage>('form');
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [createEnquiry] = useMutation(SEND_ENQUIRY);

  // Only consumed by SuccessState - memoised so it's not re-derived (URL
  // parsing + regex matching) on every keystroke while the form is open.
  const successSuburb = useMemo(
    () =>
      getEnquirySuccessSuburb({
        searchSuburbSlug,
        referrer: typeof document === 'undefined' ? '' : document.referrer,
        agencySuburb,
      }),
    [searchSuburbSlug, agencySuburb],
  );

  // Lives here, not inside EnquiryForm, so entered values survive the
  // form -> sending -> form round-trip on a failed submission (EnquiryForm
  // itself unmounts/remounts across stage changes).
  const formik = useFormik<EnquiryFormValues>({
    initialValues: ENQUIRY_FORM_INITIAL_VALUES,
    validationSchema: enquiryValidationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (formValues) => {
      setStage('sending');
      setSubmissionFailed(false);

      const input = {
        enquiryType: 'agentProfile',
        source: 'website',
        subject: '[domain.com.au] User enquiry from domain.com.au',
        referenceId: contactId,
        firstName: sanitizeForEmail(formValues.firstName),
        lastName: sanitizeForEmail(formValues.lastName),
        email: sanitizeForEmail(formValues.email),
        postcode: parseInt(formValues.postcode, 10),
        message: sanitizeForEmail(
          joinEnquiryMessage({
            enquiryTypes: formValues.enquiryTypes,
            message: formValues.message,
          }),
        ),
        ...(formValues.phone && {
          phoneNumber: sanitizeForEmail(formValues.phone),
        }),
      };

      try {
        await createEnquiry({ variables: { input } });
        setStage('success');
      } catch (err) {
        handleError(err);
        setStage('form');
        setSubmissionFailed(true);
      }
    },
  });

  return (
    <Modal closeCallback={onClose} css={styles.modalOverride}>
      <div css={styles.modalWindow} data-testid="enquiry-modal-2026">
        {stage === 'form' && (
          <>
            <EnquiryModalHeader
              agentName={agentName}
              jobTitle={jobTitle}
              agencyName={agencyName}
              profilePhoto={profilePhoto}
              telephone={telephone}
            />
            <EnquiryForm
              formik={formik}
              submissionFailed={submissionFailed}
              onHideError={() => setSubmissionFailed(false)}
            />
          </>
        )}
        {stage === 'sending' && <SendingPlaceholder />}
        {stage === 'success' && (
          <SuccessState suburb={successSuburb} onDone={onClose} />
        )}
      </div>
    </Modal>
  );
};

export default EnquiryModal2026;
